import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, onMounted } from "vue";
  import useVuelidate from "@vuelidate/core";
  import { required, email, helpers } from "@vuelidate/validators";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { validOptionalPhoneNumber } from "@/helpers/validation";
  import Cleave from "vue-cleave-component";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import { StatesEnum } from "@/enums/StatesEnum";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import router from "@/router";
  import { BuilderInterface } from "@/types/BuilderInterface";
  import ImageCropperModal from "@/components/ui/modals/ImageCropperModal.vue";
  import { UserRoles } from "@/enums/UserRoles";
  import { UserInterface } from "@/types/UserInterface";

  interface CustomerOption {
    detailsId: string;
    companyName: string;
    adminEmail: string;
    landownerIds: string[];
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminBuilderUpdate',
  setup(__props, { expose: __expose }) {
  __expose();

  const builderId = router.currentRoute.value.params.builderId;
  const isCropperModalOpen = ref(false);
  const imageForCropper = ref("");

  const toast = useToast();
  const store = useStore();
  const loading = ref(false);
  const customerOptions = ref<CustomerOption[]>([]);
  const formFields = {
    name: "",
    email: "",
    phone: "",
    contact: "",
    address: "",
    logo: "",
    state: "",
    imageFile: {
      data: "",
      buffer: null,
    } as { data: string | ArrayBuffer; buffer: File | null },
    color: "#8f00ff",
    colorB: "#ffffff",
    colorC: "#f1f5f5",
    landownerIds: [] as string[],
  };

  const form = reactive(Object.assign({}, formFields));
  const selectedCustomersCount = computed(() => {
    return customerOptions.value.filter(isCustomerSelected).length;
  });

  const rules = {
    name: { required },
    email: { email },
    phone: {
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validOptionalPhoneNumber
      ),
    },
    contact: {},
    address: {},
    state: {},
    color: { required },
    colorB: { required },
    colorC: { required },
    landownerIds: {},
  };

  const v$ = useVuelidate(rules, form);
  const fillFormFields = (builder: BuilderInterface) => {
    form.name = builder.name;
    form.email = builder.email;
    form.phone = builder.phone;
    form.contact = builder.contact;
    form.address = builder.address;
    form.state = builder.state;
    form.logo = builder.logo;
    form.imageFile.data = builder.logo;
    form.color = builder.color;
    form.colorB = builder.colorB || builder.color;
    form.colorC = builder.colorC || formFields.colorC;
    form.landownerIds = builder.landownerIds ?? [];
  };
  const getBuilder = async () => {
    try {
      const res = await store.dispatch("builder/getBuilderById", builderId);

      fillFormFields(res.data);
    } catch (error) {
      toast.error("Error getting builder");
    }
  };

  function getLandowners() {
    store
      .dispatch("user/getUsers", {
        role: UserRoles.LANDOWNER,
      })
      .then((res) => {
        customerOptions.value = mapCustomers(res.data);
      })
      .catch(() => {
        toast.error("Failed to load customers");
      });
  }

  const uploadImage = (e: InputEvent) => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      imageForCropper.value = reader.result as string;
      isCropperModalOpen.value = true;
    };
    reader.readAsDataURL(files[0]);
    form.logo = "";
  };
  const clearForm = () => {
    Object.assign(form, formFields);
    form.landownerIds = [];
    resetVuelidateErrors(v$);
  };
  const uploadImageRequest = async () => {
    const formData = new FormData();
    const logoFile = form.imageFile;

    if (!logoFile.buffer && !logoFile.data) {
      toast.error("Please upload a logo");
      return null;
    }

    formData.append("file", logoFile.buffer as File);

    try {
      const res = await store.dispatch("builder/uploadLogo", formData);

      return res.data;
    } catch (error) {
      toast.error("An error occurred while uploading the logo");
      return null;
    }
  };
  const removeLogo = () => {
    form.imageFile = {
      data: "",
      buffer: null,
    };
    form.logo = "";
  };
  const onCropImage = (file: File) => {
    form.imageFile = {
      data: URL.createObjectURL(file),
      buffer: file,
    };
    isCropperModalOpen.value = false;
  };
  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    return isValid;
  };
  const onUpdateBuilder = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    loading.value = true;

    try {
      const logo = form.logo || (await uploadImageRequest());

      if (!logo) {
        loading.value = false;
        return;
      }

      await store.dispatch("builder/updateBuilder", {
        ...form,
        logo,
        _id: builderId,
      });

      toast.success("The builder was successfully updated");
      router.push({ name: "AdminBuilder", params: { builderId } });
    } catch (error) {
      toast.error("An error occurred while updating the builder");
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    getBuilder();
    getLandowners();
  });

  function mapCustomers(users: UserInterface[]): CustomerOption[] {
    const companyMap = new Map<string, CustomerOption>();

    users.forEach((user) => {
      const details = user.detailsId;

      if (!details || typeof details === "string" || !details._id) return;

      if (!companyMap.has(details._id)) {
        const companyName =
          "companyName" in details ? details.companyName : "Unknown Company";

        companyMap.set(details._id, {
          detailsId: details._id,
          companyName,
          adminEmail: user.email,
          landownerIds: [],
        });
      }

      companyMap.get(details._id)?.landownerIds.push(user._id);
    });

    return Array.from(companyMap.values());
  }

  function isCustomerSelected(customer: CustomerOption) {
    return customer.landownerIds.every((id) => form.landownerIds.includes(id));
  }

  function toggleCustomer(customer: CustomerOption) {
    if (isCustomerSelected(customer)) {
      form.landownerIds = form.landownerIds.filter(
        (id) => !customer.landownerIds.includes(id)
      );
      return;
    }

    form.landownerIds = Array.from(
      new Set([...form.landownerIds, ...customer.landownerIds])
    );
  }

  function checkAllCustomers() {
    form.landownerIds = Array.from(
      new Set(
        customerOptions.value.flatMap((customer) => customer.landownerIds)
      )
    );
  }

  function uncheckAllCustomers() {
    form.landownerIds = [];
  }

const __returned__ = { builderId, isCropperModalOpen, imageForCropper, toast, store, loading, customerOptions, formFields, form, selectedCustomersCount, rules, v$, fillFormFields, getBuilder, getLandowners, uploadImage, clearForm, uploadImageRequest, removeLogo, onCropImage, validateForm, onUpdateBuilder, mapCustomers, isCustomerSelected, toggleCustomer, checkAllCustomers, uncheckAllCustomers, computed, reactive, ref, onMounted, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get helpers() { return helpers }, get phoneNumberOption() { return phoneNumberOption }, get validOptionalPhoneNumber() { return validOptionalPhoneNumber }, get Cleave() { return Cleave }, get useStore() { return useStore }, get useToast() { return useToast }, get resetVuelidateErrors() { return resetVuelidateErrors }, get StatesEnum() { return StatesEnum }, DropDown, get router() { return router }, get BuilderInterface() { return BuilderInterface }, ImageCropperModal, get UserRoles() { return UserRoles }, get UserInterface() { return UserInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})